// =============================================================================
// VandieZ shipper-web -- Vehicle detail (/vehicles/:vehicleId)
// =============================================================================
// View + edit a single vehicle. The owner can:
//   * Toggle online_for_bidding (only when status === 'active').
//   * Edit cargo bay dimensions, document expiry dates, base location
//     coordinates. Heavier edits (make/model/RC/payload) are intentionally
//     locked by the backend — changing those would invalidate Vahan +
//     admin review, so the user must cancel + recreate.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Loader2,
  MapPin,
  Pencil,
  Power,
  ShieldCheck,
} from "lucide-react";
import type React from "react";
import { useEffect, useId, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { vehiclesApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { Vehicle, VehiclePatchPayload } from "@/types/vehicle";

function formatDateOnly(iso: string | null): string {
  if (iso === null) {return "—";}
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function VehicleDetailPage(): React.JSX.Element {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const { accessToken } = useAuth();

  const [v, setV] = useState<Vehicle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (accessToken === null || vehicleId === undefined) {return;}
    const controller = new AbortController();
    vehiclesApi
      .detail(accessToken, vehicleId, controller.signal)
      .then((res) => {
        setV(res);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        setError(
          err instanceof ApiError ? err.problem.detail : "Couldn't load.",
        );
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, vehicleId]);

  async function onToggleOnline(): Promise<void> {
    if (accessToken === null || v === null) {return;}
    setToggling(true);
    setActionError(null);
    try {
      const updated = await vehiclesApi.toggleOnline(accessToken, v.id);
      setV(updated);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.problem.detail : "Couldn't toggle.",
      );
    } finally {
      setToggling(false);
    }
  }

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center"
      >
        <Loader2
          aria-hidden="true"
          className="h-6 w-6 animate-spin text-muted-foreground"
        />
        <span className="sr-only">Loading vehicle…</span>
      </div>
    );
  }

  if (v === null) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error ?? "Vehicle not found."}</AlertDescription>
        </Alert>
        <Link to="/vehicles" className="mt-4 inline-block">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4" />
            Back to vehicles
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicle detail"
        actions={
          <Link to="/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Vehicles
            </Button>
          </Link>
        }
      />

      <div className="space-y-6">
        {actionError !== null ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              {v.primary_photo_url !== null ? (
                <img
                  src={v.primary_photo_url}
                  alt={`${v.make} ${v.model}`}
                  className="h-20 w-32 rounded-md object-cover"
                />
              ) : null}
              <div className="flex-1 space-y-1">
                <CardTitle className="text-xl">
                  {v.make} {v.model} · {v.year_of_manufacture}
                </CardTitle>
                <CardDescription className="font-mono">
                  {v.registration_number} · {v.vehicle_type} · {v.payload_capacity_kg} kg ·{" "}
                  {v.fuel_type}
                </CardDescription>
                <p className="text-xs">
                  {v.vahan_verified ? (
                    <span className="inline-flex items-center gap-1 text-success-subtle-foreground">
                      <ShieldCheck className="h-3 w-3" />
                      Vahan verified
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      Vahan check pending
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 border-t pt-4">
            <Button
              variant={v.online_for_bidding ? "outline" : "default"}
              size="sm"
              onClick={() => void onToggleOnline()}
              disabled={toggling || v.status !== "active"}
            >
              {toggling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : v.online_for_bidding ? (
                <Circle className="h-4 w-4" />
              ) : (
                <Power className="h-4 w-4" />
              )}
              {v.online_for_bidding ? "Take offline" : "Go online for bidding"}
            </Button>
            {v.status !== "active" ? (
              <span className="self-center text-xs text-muted-foreground">
                Only active vehicles can go online (current: {v.status}).
              </span>
            ) : null}
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setEditing((b) => !b); }}
            >
              <Pencil className="h-4 w-4" />
              {editing ? "Cancel edit" : "Edit details"}
            </Button>
          </CardContent>
        </Card>

        {editing ? (
          <EditVehicleCard
            vehicle={v}
            onCancel={() => { setEditing(false); }}
            onSaved={(updated) => {
              setV(updated);
              setEditing(false);
            }}
          />
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cargo bay</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-3">
            <Stat label="Length" value={v.cargo_bay_length_ft !== null ? `${v.cargo_bay_length_ft} ft` : "—"} />
            <Stat label="Width" value={v.cargo_bay_width_ft !== null ? `${v.cargo_bay_width_ft} ft` : "—"} />
            <Stat label="Height" value={v.cargo_bay_height_ft !== null ? `${v.cargo_bay_height_ft} ft` : "—"} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Document expiries</CardTitle>
            <CardDescription>
              Updates here also notify the admin queue if a doc is about to lapse.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Stat label="Insurance" value={formatDateOnly(v.insurance_expiry)} />
            <Stat label="Fitness" value={formatDateOnly(v.fitness_expiry)} />
            <Stat label="Permit" value={formatDateOnly(v.permit_expiry)} />
            <Stat label="PUC" value={formatDateOnly(v.puc_expiry)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Base location
            </CardTitle>
            <CardDescription>
              Used to route nearby loads to this vehicle.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-sm">
              {v.base_location_lat !== null && v.base_location_lng !== null
                ? `${v.base_location_lat}, ${v.base_location_lng}`
                : "Not set"}
            </p>
          </CardContent>
        </Card>

        {v.all_photos.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Photos</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {v.all_photos.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt="Vehicle"
                  className="aspect-video w-full rounded-md object-cover"
                />
              ))}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

function EditVehicleCard({
  vehicle,
  onCancel,
  onSaved,
}: {
  vehicle: Vehicle;
  onCancel: () => void;
  onSaved: (v: Vehicle) => void;
}): React.JSX.Element {
  const { accessToken } = useAuth();
  const [lengthFt, setLengthFt] = useState(vehicle.cargo_bay_length_ft ?? "");
  const [widthFt, setWidthFt] = useState(vehicle.cargo_bay_width_ft ?? "");
  const [heightFt, setHeightFt] = useState(vehicle.cargo_bay_height_ft ?? "");
  const [insExpiry, setInsExpiry] = useState(vehicle.insurance_expiry ?? "");
  const [fitExpiry, setFitExpiry] = useState(vehicle.fitness_expiry ?? "");
  const [perExpiry, setPerExpiry] = useState(vehicle.permit_expiry ?? "");
  const [pucExpiry, setPucExpiry] = useState(vehicle.puc_expiry ?? "");
  const [baseLat, setBaseLat] = useState(vehicle.base_location_lat ?? "");
  const [baseLng, setBaseLng] = useState(vehicle.base_location_lng ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function diffStr(orig: string | null, next: string): string | undefined {
    const o = orig ?? "";
    return o === next ? undefined : next;
  }
  function diffNum(orig: string | null, next: string): number | undefined {
    const o = orig ?? "";
    if (o === next) {return undefined;}
    const n = Number.parseFloat(next);
    return Number.isFinite(n) ? n : undefined;
  }

  async function onSave(): Promise<void> {
    if (accessToken === null) {return;}
    const body: VehiclePatchPayload = {};
    const l = diffNum(vehicle.cargo_bay_length_ft, lengthFt);
    if (l !== undefined) {body.cargo_bay_length_ft = l;}
    const w = diffNum(vehicle.cargo_bay_width_ft, widthFt);
    if (w !== undefined) {body.cargo_bay_width_ft = w;}
    const h = diffNum(vehicle.cargo_bay_height_ft, heightFt);
    if (h !== undefined) {body.cargo_bay_height_ft = h;}
    const ins = diffStr(vehicle.insurance_expiry, insExpiry);
    if (ins !== undefined && ins.length > 0) {body.insurance_expiry = ins;}
    const fit = diffStr(vehicle.fitness_expiry, fitExpiry);
    if (fit !== undefined && fit.length > 0) {body.fitness_expiry = fit;}
    const per = diffStr(vehicle.permit_expiry, perExpiry);
    if (per !== undefined && per.length > 0) {body.permit_expiry = per;}
    const puc = diffStr(vehicle.puc_expiry, pucExpiry);
    if (puc !== undefined && puc.length > 0) {body.puc_expiry = puc;}
    const lat = diffNum(vehicle.base_location_lat, baseLat);
    if (lat !== undefined) {body.base_location_lat = lat;}
    const lng = diffNum(vehicle.base_location_lng, baseLng);
    if (lng !== undefined) {body.base_location_lng = lng;}

    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const updated = await vehiclesApi.patch(accessToken, vehicle.id, body);
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof ApiError ? e.problem.detail : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Edit details</CardTitle>
        <CardDescription>
          Make / model / RC / payload aren't editable — cancel and recreate
          if they change.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-3">
        <FieldNum label="Cargo length (ft)" value={lengthFt} onChange={setLengthFt} />
        <FieldNum label="Cargo width (ft)" value={widthFt} onChange={setWidthFt} />
        <FieldNum label="Cargo height (ft)" value={heightFt} onChange={setHeightFt} />
        <FieldDate label="Insurance expiry" value={insExpiry} onChange={setInsExpiry} />
        <FieldDate label="Fitness expiry" value={fitExpiry} onChange={setFitExpiry} />
        <FieldDate label="Permit expiry" value={perExpiry} onChange={setPerExpiry} />
        <FieldDate label="PUC expiry" value={pucExpiry} onChange={setPucExpiry} />
        <FieldNum label="Base latitude" value={baseLat} onChange={setBaseLat} />
        <FieldNum label="Base longitude" value={baseLng} onChange={setBaseLng} />
        {err !== null ? (
          <Alert variant="destructive" className="sm:col-span-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
      <CardContent className="flex justify-end gap-2 border-t pt-4">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => void onSave()} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Save changes
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

function FieldNum({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  // useId keeps the label/control association unique even if the card is
  // ever rendered more than once on a page.
  const id = useId();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(e) => { onChange(e.target.value.replace(/[^\d.-]/g, "")); }}
      />
    </div>
  );
}

function FieldDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="date"
        value={value !== "" ? value.slice(0, 10) : ""}
        onChange={(e) => { onChange(e.target.value); }}
      />
    </div>
  );
}
